import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  SummarySection,
  PaperSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 13 - 클린한 2열 헤더 레이아웃 (3.html 기반)
const Template13 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  return (
    <div className={`template-13 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      <div className="px-10 py-10 text-gray-800">
        {/* Header Section */}
        <div className="flex justify-between items-start mb-5 pb-5">
          <div className="flex-1">
            <h1 className={getPlaceholderClass(hasRealData.name, "text-[34px] font-bold mb-2")}>
              {currentData.name}
            </h1>
            <p className={getPlaceholderClass(hasRealData.title, "text-base text-gray-600")}>
              {currentData.title}
            </p>
          </div>
          <div className="text-right">
            {currentData.phone && (
              <p className={getPlaceholderClass(hasRealData.phone, "text-sm mb-2")}>
                {currentData.phone}
              </p>
            )}
            {currentData.email && (
              <p className={getPlaceholderClass(hasRealData.email, "text-sm")}>
                {currentData.email}
              </p>
            )}
            {currentData.socialLinks && currentData.socialLinks.length > 0 && (
              <div className="mt-2">
                {currentData.socialLinks.map((social, index) => (
                  <p key={index} className="text-sm">
                    <a href={social.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                      {social.url.replace(/^https?:\/\/(www\.)?/, '')}
                    </a>
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Summary Section */}
        {currentData.summary && (
          <div className="mb-5">
            <SummarySection
              summary={currentData.summary}
              hasRealData={hasRealData}
              textClassName="text-sm leading-[1.7] text-justify"
            />
          </div>
        )}

        {/* Key Strengths Section */}
        {currentData.keyStrengths && currentData.keyStrengths.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-5 pb-2.5 border-b-2 border-black">
              {titles.keyStrengths}
            </h2>
            <ul className="pl-5 list-disc list-outside">
              {currentData.keyStrengths.map((strength, index) => (
                <li key={index} className={`mb-2.5 text-sm leading-relaxed ${getPlaceholderClass(hasRealData.keyStrengths)}`}>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Experience Section */}
        {currentData.experiences.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-5 pb-2.5 border-b-2 border-black">
              {titles.experience}
            </h2>
            <table className="w-full">
              <tbody>
                {currentData.experiences.map((exp, index) => (
                  <tr key={index}>
                    <td className="w-40 align-top py-2.5 pr-5">
                      <p className={getPlaceholderClass(hasRealData.experiences, "text-[15px] font-bold")}>
                        {exp.startDate} - {exp.endDate}
                      </p>
                    </td>
                    <td className="align-top py-2.5">
                      <p className={getPlaceholderClass(hasRealData.experiences, "text-base font-bold mb-1")}>
                        {exp.company} <span className="font-normal text-[15px] text-gray-600">- {exp.position}</span>
                      </p>
                      {exp.overallDescription && (
                        <p className={`text-sm leading-relaxed mb-4 whitespace-pre-line ${getPlaceholderClass(hasRealData.experiences)}`}>
                          {exp.overallDescription}
                        </p>
                      )}
                      
                      {exp.projects && exp.projects.length > 0 && (
                        <div className="space-y-4">
                          {exp.projects.map((project, projIndex) => (
                            <div key={projIndex}>
                              <p className={getPlaceholderClass(hasRealData.experiences, "text-sm font-bold mb-2")}>
                                • {project.projectName}
                              </p>
                              {project.projectDescription && (
                                <p className={`text-sm leading-relaxed ml-4 whitespace-pre-line ${getPlaceholderClass(hasRealData.experiences)}`}>
                                  {project.projectDescription}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Education Section */}
        {currentData.education.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-5 pb-2.5 border-b-2 border-black">
              {titles.education}
            </h2>
            <table className="w-full">
              <tbody>
                {currentData.education.map((edu, index) => (
                  <tr key={index}>
                    <td className="w-40 align-top py-2.5 pr-5">
                      <p className={getPlaceholderClass(hasRealData.education, "text-[15px] font-bold")}>
                        {edu.startDate} - {edu.endDate}
                      </p>
                    </td>
                    <td className="align-top py-2.5">
                      <p className={getPlaceholderClass(hasRealData.education, "text-base font-bold mb-1")}>
                        {edu.school}
                      </p>
                      <p className={getPlaceholderClass(hasRealData.education, "text-sm")}>
                        {edu.degree}{edu.major && ` (${edu.major})`}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Papers Section */}
        {hasRealData.papers && currentData.papers.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-5 pb-2.5 border-b-2 border-black">
              {titles.papers}
            </h2>
            <PaperSection
              papers={currentData.papers}
              hasRealData={hasRealData}
              getPaperTypeText={getPaperTypeText}
              itemClassName="mb-4"
              titleClassName="text-base font-bold"
              authorsClassName="text-sm text-gray-600"
              journalClassName="text-sm"
            />
          </div>
        )}

        {/* Skills & Certificates Row */}
        <div className="flex gap-10">
          {/* Skills Section */}
          {currentData.skills.length > 0 && (
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-5 pb-2.5 border-b-2 border-black">
                {titles.skills}
              </h2>
              <p className={getPlaceholderClass(hasRealData.skills, "text-sm leading-[1.8]")}>
                {currentData.skills.join(', ')}
              </p>
            </div>
          )}

          {/* Certificates Section */}
          {currentData.certificates.length > 0 && (
            <div className="flex-1">
              <h2 className="text-xl font-bold mb-5 pb-2.5 border-b-2 border-black">
                {titles.certificates}
              </h2>
              {currentData.certificates.map((cert, index) => (
                <div key={index} className="mb-3">
                  <p className={getPlaceholderClass(hasRealData.certificates, "text-sm leading-[1.8]")}>
                    {cert.name}
                  </p>
                  <p className={getPlaceholderClass(hasRealData.certificates, "text-[13px] text-gray-600")}>
                    {cert.issuer} ({cert.date})
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Template13;